import { useState, useEffect, useCallback } from "react";
import { TextField, InputAdornment, IconButton, Grid } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import moment from "moment-timezone";
import { makeRequest } from "../../../assets/functions_helper";

// Componente de tarjeta de estadística mejorado
const StatCard = ({ title, value, subtitle, icon, color = "blue" }) => {
  const colorStyles = {
    blue: {
      bg: "bg-blue-50",
      border: "border-blue-200",
      text: "text-blue-700",
      icon: "text-blue-500",
    },
    green: {
      bg: "bg-green-50",
      border: "border-green-200",
      text: "text-green-700",
      icon: "text-green-500",
    },
    purple: {
      bg: "bg-purple-50",
      border: "border-purple-200",
      text: "text-purple-700",
      icon: "text-purple-500",
    },
    yellow: {
      bg: "bg-yellow-50",
      border: "border-yellow-200",
      text: "text-yellow-700",
      icon: "text-yellow-500",
    },
    red: {
      bg: "bg-red-50",
      border: "border-red-200",
      text: "text-red-700",
      icon: "text-red-500",
    },
    gray: {
      bg: "bg-gray-50",
      border: "border-gray-200",
      text: "text-gray-700",
      icon: "text-gray-500",
    },
  };

  const styles = colorStyles[color] || colorStyles.blue;

  return (
    <div
      className={`${styles.bg} ${styles.border} rounded-xl border-2 p-5 shadow-sm hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1`}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className={`text-xs font-semibold uppercase tracking-wider ${styles.text} opacity-80 mb-1`}>
            {title}
          </p>
          <p className={`text-3xl font-black ${styles.text}`}>{value}</p>
          {subtitle && <p className="text-xs mt-2 text-gray-500">{subtitle}</p>}
        </div>
        <div className={`text-4xl ${styles.icon} opacity-80`}>{icon}</div>
      </div>
    </div>
  );
};

const formatCurrency = (amount) => {
  if (!amount) return "$0";
  return new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    minimumFractionDigits: 0,
  }).format(amount);
};

export default function RestaurantStatistics({ restaurantId }) {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(() => moment().utc().format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState(() => moment().utc().format("YYYY-MM-DD"));

  // Función memoizada para obtener estadísticas
  const fetchAnalytics = useCallback(async () => {
    if (!restaurantId) return;
    setLoading(true);
    try {
      const start = moment.utc(startDate).startOf("day").format("YYYY-MM-DDTHH:mm:ss") + "Z";
      const end = moment.utc(endDate).endOf("day").format("YYYY-MM-DDTHH:mm:ss") + "Z";

      const url = `restaurants/analytics/${restaurantId}?client_timezone=America/Bogota&start_date=${encodeURIComponent(
        start
      )}&end_date=${encodeURIComponent(end)}`;

      const response = await makeRequest(
        "GET",
        url,
        null,
        "application/json",
        localStorage.getItem("token"),
        localStorage.getItem("tokenType")
      );
      setAnalytics(response);
    } catch (error) {
      console.error("Error obteniendo analytics:", error);
    } finally {
      setLoading(false);
    }
  }, [restaurantId, startDate, endDate]);

  // Efecto para cargar datos al montar o cuando cambian las dependencias
  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const handleStartDateChange = (e) => {
    setStartDate(e.target.value);
  };

  const handleEndDateChange = (e) => {
    setEndDate(e.target.value);
  };

  if (loading) {
    return null;
  }

  if (!analytics) {
    return (
      <div className="text-center p-10 text-gray-500 bg-white rounded-xl shadow-sm max-w-6xl mx-auto">
        No hay datos estadísticos disponibles para el rango seleccionado.
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100 max-w-6xl mx-auto">
      {/* Cabecera con filtros */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-2">
          <span className="text-3xl">📊</span>
          <h2 className="text-2xl font-bold text-gray-800">Estadísticas del Restaurante</h2>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <TextField
            label="Fecha inicio"
            type="date"
            value={startDate}
            onChange={handleStartDateChange}
            InputLabelProps={{ 
              shrink: true,
              sx: { color: "#374151", fontWeight: 500 }
            }}
            inputProps={{
              sx: { color: "#111827" }
            }}
            size="small"
            sx={{
              minWidth: 160,
              "& .MuiOutlinedInput-root": {
                "& fieldset": { borderColor: "#E5E7EB" },
                "&:hover fieldset": { borderColor: "#9CA3AF" },
                "&.Mui-focused fieldset": { borderColor: "#2563EB" },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <CalendarTodayIcon fontSize="small" sx={{ color: "#6B7280" }} />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            label="Fecha fin"
            type="date"
            value={endDate}
            onChange={handleEndDateChange}
            InputLabelProps={{ 
              shrink: true,
              sx: { color: "#374151", fontWeight: 500 }
            }}
            inputProps={{
              sx: { color: "#111827" }
            }}
            size="small"
            sx={{
              minWidth: 160,
              "& .MuiOutlinedInput-root": {
                "& fieldset": { borderColor: "#E5E7EB" },
                "&:hover fieldset": { borderColor: "#9CA3AF" },
                "&.Mui-focused fieldset": { borderColor: "#2563EB" },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <CalendarTodayIcon fontSize="small" sx={{ color: "#6B7280" }} />
                </InputAdornment>
              ),
            }}
          />
          <IconButton
            onClick={fetchAnalytics}  // Ahora el botón refresca los datos
            sx={{
              bgcolor: "#2563EB",
              color: "white",
              "&:hover": { bgcolor: "#1D4ED8" },
              borderRadius: "8px",
              padding: "8px",
            }}
          >
            <RefreshIcon />
          </IconButton>
        </div>
      </div>

      {/* Grid de tarjetas */}
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            title="TIEMPO PROMEDIO DE RESOLUCIÓN"
            value={`${analytics.avg_resolve_time ? Math.round(analytics.avg_resolve_time / 60) : 0} min`}
            subtitle="Por llamada de mesero"
            color="blue"
            icon="⏱️"
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            title="DURACIÓN PROMEDIO DE SESIÓN"
            value={`${analytics.avg_session_length ? Math.round(analytics.avg_session_length / 60) : 0} min`}
            subtitle="Tiempo promedio por cliente"
            color="green"
            icon="🕐"
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            title="SESIONES EN EL RANGO"
            value={analytics.current_day_sessions || 0}
            subtitle="Clientes atendidos"
            color="purple"
            icon="👥"
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            title="GASTO PROMEDIO"
            value={formatCurrency(analytics.avg_money_spent || 0)}
            subtitle="Por sesión de cliente"
            color="yellow"
            icon="💰"
          />
        </Grid>
      </Grid>

      {/* Día con más sesiones */}
      {analytics.top_session_days && analytics.top_session_days.length > 0 && (
        <div className="mt-6 p-4 bg-amber-50 rounded-xl border border-amber-200 flex items-center gap-3">
          <span className="text-2xl">🏆</span>
          <p className="text-sm text-amber-800">
            <span className="font-semibold">Día con más sesiones en el rango:</span>{" "}
            {moment(analytics.top_session_days[0].date).format("DD/MM/YYYY")}
            <span className="ml-2 font-bold">({analytics.top_session_days[0].count} sesiones)</span>
          </p>
        </div>
      )}
    </div>
  );
}